import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import theme from '../../theme';
import config from '../../config';

const SignInModal = ({ isOpen, onClose, onLoginSuccess, buttonRef }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState('');
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) &&
          buttonRef?.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLogging(true);

    try {
      const loginRes = await fetch(`${config.API_URL}/donators/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      if (!loginRes.ok) {
        const errorData = await loginRes.json();
        throw new Error(errorData.detail || 'Invalid email or password');
      }

      const loginData = await loginRes.json();
      console.log('Donator logged in:', loginData.donator);

      if (onLoginSuccess) {
        onLoginSuccess(loginData.donator);
      }

      // Close modal and reload to update state
      onClose();
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setLogging(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        backgroundColor: theme.colors.bg.card,
        borderRadius: theme.borderRadius.lg,
        border: `1px solid ${theme.colors.border.default}`,
        padding: theme.spacing[6],
        width: '320px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
        zIndex: theme.zIndex.dropdown
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing[4]
      }}>
        <h2 style={{
          fontSize: theme.typography.fontSize.lg,
          fontWeight: theme.typography.fontWeight.semibold,
          color: theme.colors.text.primary,
          margin: 0
        }}>
          Sign In
        </h2>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.text.secondary,
            cursor: 'pointer',
            padding: theme.spacing[1],
            fontSize: '20px',
            lineHeight: 1,
            transition: `color ${theme.transitions.base}`
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.text.primary}
          onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.text.secondary}
        >
          ×
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: 'rgba(220, 38, 38, 0.1)',
          border: `1px solid rgba(220, 38, 38, 0.5)`,
          color: '#fca5a5',
          padding: theme.spacing[2],
          borderRadius: theme.borderRadius.md,
          marginBottom: theme.spacing[4],
          fontSize: theme.typography.fontSize.sm
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[3] }}>
        <input
          style={{
            width: '100%',
            padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
            backgroundColor: theme.colors.bg.primary,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.md,
            color: theme.colors.text.primary,
            fontSize: theme.typography.fontSize.sm,
            outline: 'none',
            transition: `border-color ${theme.transitions.base}`
          }}
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          required
          onFocus={(e) => e.currentTarget.style.borderColor = theme.colors.primary[500]}
          onBlur={(e) => e.currentTarget.style.borderColor = theme.colors.border.default}
        />

        <input
          style={{
            width: '100%',
            padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
            backgroundColor: theme.colors.bg.primary,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.md,
            color: theme.colors.text.primary,
            fontSize: theme.typography.fontSize.sm,
            outline: 'none',
            transition: `border-color ${theme.transitions.base}`
          }}
          type="password"
          name="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange}
          required
          onFocus={(e) => e.currentTarget.style.borderColor = theme.colors.primary[500]}
          onBlur={(e) => e.currentTarget.style.borderColor = theme.colors.border.default}
        />

        <button
          style={{
            width: '100%',
            padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
            backgroundColor: theme.colors.primary[500],
            color: '#fff',
            border: 'none',
            borderRadius: theme.borderRadius.md,
            cursor: logging ? 'not-allowed' : 'pointer',
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold,
            transition: `background-color ${theme.transitions.base}`,
            opacity: logging ? 0.6 : 1
          }}
          type="submit"
          disabled={logging}
          onMouseEnter={(e) => {
            if (!logging) e.currentTarget.style.backgroundColor = theme.colors.primary[600];
          }}
          onMouseLeave={(e) => {
            if (!logging) e.currentTarget.style.backgroundColor = theme.colors.primary[500];
          }}
        >
          {logging ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      <div style={{
        textAlign: 'center',
        fontSize: theme.typography.fontSize.xs,
        color: theme.colors.text.muted,
        marginTop: theme.spacing[4],
        paddingTop: theme.spacing[3],
        borderTop: `1px solid ${theme.colors.border.default}`
      }}>
        <p style={{ margin: 0, marginBottom: theme.spacing[2] }}>
          Don't have an account?
        </p>
        <button
          onClick={() => {
            onClose();
            navigate('/donator-signup');
          }}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.primary[500],
            textDecoration: 'none',
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            transition: `color ${theme.transitions.base}`,
            cursor: 'pointer',
            padding: 0
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.primary[400]}
          onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.primary[500]}
        >
          Create Account
        </button>
      </div>
    </div>
  );
};

export default SignInModal;